import { format } from "date-fns";

import { ROUTES } from "@config/app.routing";

import {
  AGENT_PREFERENCE,
  B2BRegisterClientPayload,
  B2BRegisterCompanyPayload,
  EXTRA_SERVICES_B2B,
  FREQUENCY,
} from "@typings/b2b";
import { PAYMENT_METHODS } from "@typings/globals";

import { nullifyString } from "@services/NullifyString";
import { generateReturnUrl } from "@services/ProvidePaymentUrls";
import { DATE_FORMAT } from "@services/DateFormatter";

import { IStore } from "@app/redux/reducers";

import {
  getRegistrationForm,
  getPreselectedAgentUuid,
  getSelectedCleaningFrequency,
  getSelectedTasksList,
  getServiceLocationId,
  getAgentPreference,
  getReservationUuid,
  getPetsInfo,
  getWeekStartDate,
  getPremises,
  getSurfaceArea,
  getPremisesEquipmentsByType,
  getHasCleaningMaterials,
  getContactAfterBooking,
  getNonStandardFrequencyShifts,
  getLocationUuid,
  getSelectedPaymentMethod,
  getLocationComment,
  getSpecialInstructions,
  getKeyPick,
  getVoucher,
  getPremisesServicesSelected,
  getIDontKnowDurationNotification,
  getIDontKnowFrequencyNotification,
  getTask,
  getAdditionalInfo,
  getSelectedPricePlanUuid,
  getExecutionDateTime,
  getExecution,
} from "./b2b.selectors";

export const getAddLocationEndpointRequest = (store: IStore) => {
  const form = getRegistrationForm(store);

  return {
    serviceLocationId: getServiceLocationId(store),
    address: form.get("address"),
    floorAndDoor: form.get("floorAndDoor"),
    entryCode: form.get("entryCode"),
  };
};

export const getAvailabilityEndpointRequest = (
  store: IStore,
  serviceLocationId: number,
  customWeekStartDate?: string,
) => {
  const tasks = getSelectedTasksList(store).map(item => ({
    service: item.get("service"),
    duration: item.get("duration"),
  }));
  const premisesServices =
    getPremisesServicesSelected(store)?.map(item => ({
      service: item.get("service"),
      duration: item.get("duration"),
    })) || [];
  const agentPreference = getAgentPreference(store);
  const reservationUuid = getReservationUuid(store);
  const weekStartDate = customWeekStartDate ?? getWeekStartDate(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);

  return {
    serviceLocationId,
    frequency: getSelectedCleaningFrequency(store),
    hasPets: getPetsInfo(store),
    agentPreference,
    tasks: tasks.concat(premisesServices),
    ...(reservationUuid && { reservationUuid }),
    ...(weekStartDate && { weekStartDate }),
    ...(preselectedAgentUuid &&
      agentPreference === AGENT_PREFERENCE.PRESELECTED && {
        preselectedAgentUuid,
      }),
  };
};

export const getReservationEndpointRequest = (store: IStore) => {
  const frequency = getSelectedCleaningFrequency(store);
  const tasks = getSelectedTasksList(store).map(item => ({
    service: item.get("service"),
    duration: frequency === FREQUENCY.MORE_OFTEN ? null : item.get("duration"),
  }));
  const premisesServices =
    getPremisesServicesSelected(store)?.map(item => ({
      service: item.get("service"),
      duration: item.get("duration"),
    })) || [];
  const premises = getPremises(store);
  const agentPreference = getAgentPreference(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);
  const surfaceArea = Number(getSurfaceArea(store));
  const premisesEquipments = getPremisesEquipmentsByType(store, premises);
  const execution = getExecution(store);

  return {
    premises,
    serviceLocationId: getServiceLocationId(store),
    executionDateTime: getExecutionDateTime(store),
    pricePlanUuid: getSelectedPricePlanUuid(store),
    frequency,
    agentPreference,
    tasks: tasks.concat(premisesServices),
    preselectedAgentUuid,
    ...(surfaceArea && { surfaceArea }),
    ...(surfaceArea && { premisesEquipments }),
    hasCleaningMaterials: getHasCleaningMaterials(store),
    contactAfterBooking: getContactAfterBooking(store),
    ...(frequency === FREQUENCY.MORE_OFTEN && {
      nonStandardFrequencyShifts: getNonStandardFrequencyShifts(store),
    }),
    ...(frequency === FREQUENCY.MORE_OFTEN && {
      nonStandardFrequencyContractStartDate: execution.date,
    }),
  };
};

export const getPricingEndpointRequest = (store: IStore) => {
  const executionDateTime = getExecutionDateTime(store);
  const frequency = getSelectedCleaningFrequency(store);
  const tasks = getSelectedTasksList(store).map(item => ({
    service: item.get("service"),
    duration: frequency === FREQUENCY.MORE_OFTEN ? null : item.get("duration"),
  }));
  const premisesServices =
    getPremisesServicesSelected(store)?.map(item => ({
      service: item.get("service"),
      duration: item.get("duration"),
    })) || [];

  return {
    serviceLocationId: getServiceLocationId(store),
    pricePlanUuid: getSelectedPricePlanUuid(store),
    tasks: tasks.concat(premisesServices),
    frequency,
    executionDateTime: executionDateTime
      ? format(
          new Date(executionDateTime),
          DATE_FORMAT.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND_ISO_8601,
        )
      : null,
    voucherToken: getVoucher(store),
    ...(frequency === FREQUENCY.MORE_OFTEN && {
      nonStandardFrequencyShifts: getNonStandardFrequencyShifts(store),
    }),
  };
};

export const getRegisterClientEndpointRequest = (
  store: IStore,
): B2BRegisterClientPayload => {
  const form = getRegistrationForm(store);
  const selectedPaymentMethod = getSelectedPaymentMethod(store);

  return {
    company: {
      name: form.get("name"),
      phone: form.get("phone"),
      vat: nullifyString(form.get("vat")),
      sdi: nullifyString(form.get("sdi")),
      email: form.get("email"),
      location: {
        address: form.get("address"),
        serviceLocationId: getServiceLocationId(store),
        floorAndDoor: form.get("floorAndDoor"),
        entryCode: form.get("entryCode"),
      },
    },
    contactPersonFirstName: form.get("contactPersonFirstName"),
    contactPersonLastName: form.get("contactPersonLastName"),
    contactPersonPhone: form.get("contactPersonPhone"),
    contactPersonEmail: form.get("contactPersonEmail"),
    loginEmail: form.get("loginEmail"),
    password: form.get("password"),
    marketingConsent: form.get("marketingConsent"),
    invoicePayment: selectedPaymentMethod === PAYMENT_METHODS.MONTHLY_INVOICE,
  };
};

export const getRegisterCompanyEndpointRequest = (
  store: IStore,
): B2BRegisterCompanyPayload => {
  const form = getRegistrationForm(store);

  return {
    name: form.get("name"),
    phone: form.get("phone"),
    vat: nullifyString(form.get("vat")),
    sdi: nullifyString(form.get("sdi")),
    email: form.get("email"),
    location: {
      address: form.get("address"),
      serviceLocationId: getServiceLocationId(store),
      floorAndDoor: form.get("floorAndDoor"),
      entryCode: form.get("entryCode"),
    },
  };
};

export const getCheckoutEndpointRequest = (store: IStore) => {
  const voucherToken = getVoucher(store);
  const returnUrl = generateReturnUrl({
    originRoute: ROUTES.B2B_CHECKOUT,
    targetRoute: `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
  });

  return {
    returnUrl,
    keyPick: getKeyPick(store),
    locationComment: getLocationComment(store),
    specialInstructions: getSpecialInstructions(store),
    locationUuid: getLocationUuid(store),
    paymentMethod: getSelectedPaymentMethod(store),
    ...(voucherToken ? { voucherToken } : {}),
  };
};

export const getGetAQuoteEndpointRequest = (store: IStore) => {
  const form = getRegistrationForm(store);
  const premises = getPremises(store);
  const surfaceArea = getSurfaceArea(store);
  const frequency = getSelectedCleaningFrequency(store);
  const nonStandardFrequencyShifts = getNonStandardFrequencyShifts(store);
  const iDontKnowDuration = getIDontKnowDurationNotification(store);
  const iDontKnowFrequency = getIDontKnowFrequencyNotification(store);
  const quoteTask = getTask(store, EXTRA_SERVICES_B2B.B2B_CLEANING);
  const additionalInfo = getAdditionalInfo(store);

  return {
    company: {
      name: form.get("name"),
      phone: form.get("phone"),
      vat: nullifyString(form.get("vat")),
      email: form.get("email"),
      location: {
        address: form.get("address"),
        serviceLocationId: getServiceLocationId(store),
        entryCode: form.get("entryCode"),
      },
    },
    contactPersonFirstName: form.get("contactPersonFirstName"),
    contactPersonLastName: form.get("contactPersonLastName"),
    companyAdditionalInformation: additionalInfo,
    premises,
    surfaceArea,
    frequency,
    numberOfNonStandardFrequencyShifts: nonStandardFrequencyShifts.length,
    duration: quoteTask.get("duration"),
    iDontKnowFrequency,
    iDontKnowDuration,
  };
};
